import Hint from '../../../../../components/common/Hint';

/**
 * Pill-style tab group: one option is always selected, the rest wait to be picked.
 * Shares its look with the output-format picker so every "choose one" control in a
 * tool's settings column reads the same.
 *
 * `options` — `[{ id, label, icon?: Component, disabled?, hint? }]`; `hint` is shown in the app's
 * tooltip, and also on a disabled option (say why it is unavailable).
 * `columns` — how many per row (defaults to all in one row).
 * `stacked` — icon above the label, the look of the output-format picker (default: icon beside it).
 */
export default function SegmentedTabs({
    options,
    value,
    onChange,
    disabled = false,
    columns,
    ariaLabel,
    stacked = false,
}) {
    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            className="grid gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800"
            style={{ gridTemplateColumns: `repeat(${columns || options.length}, minmax(0, 1fr))` }}
        >
            {options.map((opt) => {
                const active = value === opt.id;
                const Icon = opt.icon;
                const button = (
                    <button
                        key={opt.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={disabled || opt.disabled}
                        onClick={() => onChange(opt.id)}
                        className={`flex items-center justify-center rounded-lg leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
                            stacked
                                ? 'min-h-[54px] flex-col gap-1 px-1 py-1.5 text-[11px]'
                                : 'min-h-[36px] gap-1.5 px-2 py-1.5 text-[12.5px] font-semibold'
                        } ${
                            active
                                ? `bg-white text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400 ${stacked ? 'font-semibold' : ''}`
                                : `text-[#6B635C] hover:text-[#1C1815] dark:text-zinc-400 dark:hover:text-zinc-200 ${stacked ? 'font-medium' : ''}`
                        }`}
                    >
                        {Icon ? <Icon size={stacked ? 20 : 15} stroke={stacked ? 1.8 : 1.9} /> : null}
                        {opt.label}
                    </button>
                );
                // A disabled button swallows hover, so the tooltip hangs off a wrapper.
                return opt.hint
                    ? (
                        <Hint key={opt.id} label={opt.hint}>
                            <span className="flex min-w-0 [&>button]:w-full">{button}</span>
                        </Hint>
                    )
                    : button;
            })}
        </div>
    );
}
